<template>
  <div class="sales-table-wrapper">
    <modal-box
      :is-active="isModalActive"
      :trash-object-name="trashObjectName"
      @confirm="trashConfirm"
      @cancel="trashCancel"
    />

    <!-- Search Panel -->
    <div class="search-panel">
      <div class="search-panel-header" @click="searchPanelOpen = !searchPanelOpen">
        <span class="search-panel-title">
          <span class="search-icon-bubble" :class="{ 'has-active': activeSearchCount > 0 }">
            <b-icon icon="magnify" size="is-small" />
          </span>
          Cautare
          <span v-if="activeSearchCount > 0" class="tag is-info is-light ml-2">
            {{ activeSearchCount }} activ{{ activeSearchCount > 1 ? 'e' : '' }}
          </span>
        </span>
        <b-icon :icon="searchPanelOpen ? 'chevron-up' : 'chevron-down'" size="is-small" />
      </div>

      <transition name="panel-slide">
        <div v-if="searchPanelOpen" class="search-panel-body">
          <div class="search-grid">
            <b-field label="Vanzator">
              <b-input v-model="searchTerms.sellerFirstNameLastName" @input="onSearchInput" placeholder="Cauta..." icon="magnify" size="is-small" clearable />
            </b-field>
            <b-field label="Adresa vanzator">
              <b-input v-model="searchTerms.sellerAddress" @input="onSearchInput" placeholder="Cauta..." icon="magnify" size="is-small" clearable />
            </b-field>
            <b-field label="Nr. carte funciara">
              <b-input v-model="searchTerms.landRegistryNumber" @input="onSearchInput" placeholder="Cauta..." icon="magnify" size="is-small" clearable />
            </b-field>
            <b-field label="Categorie folosinta">
              <b-input v-model="searchTerms.category" @input="onSearchInput" placeholder="Cauta..." icon="magnify" size="is-small" clearable />
            </b-field>
            <b-field label="Tarla">
              <b-input v-model="searchTerms.tarla" @input="onSearchInput" placeholder="Cauta..." icon="magnify" size="is-small" clearable />
            </b-field>
            <b-field label="Parcela">
              <b-input v-model="searchTerms.parcela" @input="onSearchInput" placeholder="Cauta..." icon="magnify" size="is-small" clearable />
            </b-field>
            <b-field label="Cumparator">
              <b-input v-model="searchTerms.buyer" @input="onSearchInput" placeholder="Cauta..." icon="magnify" size="is-small" clearable />
            </b-field>
          </div>
          <div class="search-panel-actions">
            <button class="button is-small is-light" @click="clearSearch" :disabled="activeSearchCount === 0">
              <b-icon icon="close" size="is-small" />
              <span>Reseteaza</span>
            </button>
          </div>
        </div>
      </transition>
    </div>

    <!-- Table -->
    <b-table
      :checked-rows.sync="checkedRows"
      :loading="isLoading"
      :paginated="true"
      :per-page="100"
      :striped="true"
      :hoverable="true"
      :data="offers"
    >
      <b-table-column label="Nr.crt." field="nrCrt" sortable centered v-slot="props">
        {{ props.row.nrCrt }}
      </b-table-column>

      <b-table-column label="Data inregistrarii" field="registrationDate" sortable centered v-slot="props">
        {{ props.row.registrationDate | formatDate }}
      </b-table-column>

      <b-table-column label="Vanzator" field="sellerFirstNameLastName" sortable centered v-slot="props">
        {{ props.row.sellerFirstNameLastName }}
      </b-table-column>

      <b-table-column label="Adresa vanzator" field="sellerAddress" sortable centered v-slot="props">
        <div class="text-cell" :title="props.row.sellerAddress">{{ props.row.sellerAddress }}</div>
      </b-table-column>

      <b-table-column label="Suprafata (mp)" field="surface" sortable centered v-slot="props">
        {{ props.row.surface }}
      </b-table-column>

      <b-table-column label="Pret vanzare" field="price" sortable centered v-slot="props">
        {{ props.row.price }}
      </b-table-column>

      <b-table-column label="Cumparator" field="buyer" sortable centered v-slot="props">
        {{ props.row.buyer }}
      </b-table-column>

      <!-- Actions -->
      <b-table-column centered width="100" v-slot="props">
        <div class="row-actions">
          <b-tooltip label="Vizualizeaza" position="is-top" type="is-dark" :delay="300">
            <button class="button is-small is-light preview-btn" @click.stop="openPreview(props.row)">
              <b-icon icon="eye" size="is-small" />
            </button>
          </b-tooltip>
          <router-link
            :to="{ name: 'sale.edit', params: { id: props.row.id } }"
            class="button is-small is-primary"
            title="Editare"
          >
            <b-icon icon="file-edit" size="is-small" />
          </router-link>
          <button
            class="button is-small is-danger"
            type="button"
            @click.stop="trashModal(props.row)"
          >
            <b-icon icon="trash-can" size="is-small" />
          </button>
        </div>
      </b-table-column>

      <section class="section" slot="empty">
        <div class="content has-text-grey has-text-centered">
          <template v-if="isLoading">
            <p><b-icon icon="dots-horizontal" size="is-large" /></p>
            <p>Se incarca datele...</p>
          </template>
          <template v-else>
            <p><b-icon icon="emoticon-sad" size="is-large" /></p>
            <p>Nu exista inregistrari</p>
          </template>
        </div>
      </section>
    </b-table>

    <!-- Preview Modal -->
    <b-modal :active.sync="previewOpen" :width="600" scroll="keep" :can-cancel="['escape', 'outside']">
      <div class="preview-card" v-if="previewEntry">
        <div class="preview-header">
          <div class="preview-header-left">
            <span class="preview-nr">Nr. {{ previewEntry.nrCrt }}</span>
            <span class="preview-date">{{ previewEntry.registrationDate | formatDate }}</span>
          </div>
        </div>

        <div class="preview-body">
          <div class="preview-field">
            <span class="preview-label">Vanzator</span>
            <span class="preview-value">{{ previewEntry.sellerFirstNameLastName || '—' }}</span>
          </div>
          <div class="preview-field">
            <span class="preview-label">Adresa vanzator</span>
            <span class="preview-value">{{ previewEntry.sellerAddress || '—' }}</span>
          </div>
          <div class="preview-field">
            <span class="preview-label">Suprafata teren (mp)</span>
            <span class="preview-value">{{ previewEntry.surface || '—' }}</span>
          </div>
          <div class="preview-field">
            <span class="preview-label">Nr. carte funciara</span>
            <span class="preview-value">{{ previewEntry.landRegistryNumber || '—' }}</span>
          </div>
          <div class="preview-field">
            <span class="preview-label">Categorie folosinta</span>
            <span class="preview-value">{{ previewEntry.category || '—' }}</span>
          </div>
          <div class="preview-field">
            <span class="preview-label">Pret de vanzare</span>
            <span class="preview-value">{{ previewEntry.price || '—' }}</span>
          </div>
          <div class="preview-field">
            <span class="preview-label">Tarla</span>
            <span class="preview-value">{{ previewEntry.tarla || '—' }}</span>
          </div>
          <div class="preview-field">
            <span class="preview-label">Parcela</span>
            <span class="preview-value">{{ previewEntry.parcela || '—' }}</span>
          </div>
          <div class="preview-field">
            <span class="preview-label">Cumparator</span>
            <span class="preview-value">{{ previewEntry.buyer || '—' }}</span>
          </div>
          <div class="preview-field">
            <span class="preview-label">Proces verbal procedura</span>
            <span class="preview-value">{{ previewEntry.procesVerbalProcedura ? $options.filters.formatDate(previewEntry.procesVerbalProcedura) : '—' }}</span>
          </div>
          <div class="preview-field">
            <span class="preview-label">Proces verbal finalizare</span>
            <span class="preview-value">{{ previewEntry.procesVerbalFinalizare ? $options.filters.formatDate(previewEntry.procesVerbalFinalizare) : '—' }}</span>
          </div>
        </div>

        <div class="preview-footer">
          <router-link
            :to="{ name: 'sale.edit', params: { id: previewEntry.id } }"
            class="button is-primary"
          >
            <b-icon icon="file-edit" size="is-small" />
            <span>Editare</span>
          </router-link>
          <button class="button is-light" @click="previewOpen = false">Inchide</button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import ModalBox from "@/components/ModalBox";
import debounce from "lodash/debounce";

const SEARCH_FIELDS = [
  "sellerFirstNameLastName",
  "sellerAddress",
  "landRegistryNumber",
  "category",
  "tarla",
  "parcela",
  "buyer",
];

export default {
  name: "SalesTable",
  components: { ModalBox },
  props: {
    offers: { type: Array, default: null },
    checkable: { type: Boolean, default: false },
    isLoading: { type: Boolean, default: false },
  },
  data() {
    return {
      isModalActive: false,
      trashObject: null,
      checkedRows: [],
      searchTerms: Object.fromEntries(SEARCH_FIELDS.map((f) => [f, ""])),
      searchPanelOpen: false,
      previewOpen: false,
      previewEntry: null,
    };
  },
  computed: {
    activeSearchCount() {
      return Object.values(this.searchTerms).filter((v) => v && v.trim() !== "").length;
    },
    trashObjectName() {
      if (this.trashObject) {
        return "parcela " + this.trashObject.parcela + ", tarla " + this.trashObject.tarla;
      }
      return null;
    },
  },
  created() {
    this.emitSearch = debounce(() => {
      this.$emit("search", { ...this.searchTerms });
    }, 300);
  },
  methods: {
    onSearchInput() {
      this.emitSearch();
    },
    clearSearch() {
      SEARCH_FIELDS.forEach((f) => this.$set(this.searchTerms, f, ""));
      this.$emit("search", { ...this.searchTerms });
    },
    openPreview(row) {
      this.previewEntry = row;
      this.previewOpen = true;
    },
    trashModal(trashObject) {
      this.trashObject = trashObject;
      this.isModalActive = true;
    },
    trashConfirm() {
      this.isModalActive = false;
      this.$emit("handleDeleteSale", this.trashObject.id);
    },
    trashCancel() {
      this.isModalActive = false;
    },
  },
};
</script>

<style scoped>
/* Search Panel */
.search-panel {
  border: 1px solid #dbdbdb;
  border-radius: 6px;
  margin-bottom: 1rem;
  background: #fff;
  overflow: hidden;
}

.search-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.65rem 1rem;
  cursor: pointer;
  user-select: none;
  background: #fafafa;
  transition: background 0.15s;
}

.search-panel-header:hover {
  background: #f0f0f0;
}

.search-panel-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  font-size: 0.9rem;
}

.search-icon-bubble {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: #3273dc;
  color: #fff;
  flex-shrink: 0;
  transition: background-color 0.2s;
}

.search-icon-bubble.has-active {
  background-color: #2160c4;
  box-shadow: 0 0 0 3px rgba(50, 115, 220, 0.25);
}

.search-panel-body {
  padding: 1rem 1rem 0.75rem;
  border-top: 1px solid #f0f0f0;
}

.search-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.5rem 1rem;
}

.search-grid :deep(.label) {
  font-size: 0.75rem;
  margin-bottom: 0.25rem;
}

.search-panel-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 0.75rem;
}

.panel-slide-enter-active,
.panel-slide-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.panel-slide-enter,
.panel-slide-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* Table */
.text-cell {
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.row-actions {
  display: flex;
  gap: 0.35rem;
  justify-content: center;
}

.preview-btn:hover {
  background-color: #e8f0fe;
  color: #3273dc;
  border-color: #3273dc;
}

/* Preview Modal */
.preview-card {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
}

.preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #ededed;
}

.preview-header-left {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.preview-nr {
  font-size: 1.15rem;
  font-weight: 700;
  color: #363636;
}

.preview-date {
  font-size: 0.8rem;
  color: #7a7a7a;
}

.preview-body {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem 1.5rem;
  padding: 1.25rem 1.5rem;
}

.preview-field {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.preview-label {
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #9a9a9a;
}

.preview-value {
  font-size: 0.92rem;
  color: #363636;
  line-height: 1.45;
}

.preview-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #ededed;
  background: #fafafa;
}
</style>
